import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IReferrer } from '@clickersphere/core/entities/IReferrer';
import {
  DEFAULT_REFERRER_KIND,
  ReferrerKindEnum,
} from '@clickersphere/core/enums/ReferrerKindEnum';
import { useCreateReferrer } from '../../../app/hooks/referrer/useCreateReferrer';
import { useDeleteReferrer } from '../../../app/hooks/referrer/useDeleteReferrer';
import { useReferrers } from '../../../app/hooks/referrer/useReferrers';
import { useUpdateReferrer } from '../../../app/hooks/referrer/useUpdateReferrer';
import useToast from '../../../app/hooks/useToast';

export interface IReferrerFormValues {
  name: string;
  kind: ReferrerKindEnum;
  phone: string;
  instagram: string;
}

const EMPTY_FORM: IReferrerFormValues = {
  name: '',
  kind: DEFAULT_REFERRER_KIND,
  phone: '',
  instagram: '',
};

interface IUseReferrerComboboxController {
  value?: string | null;
  onChange: (referrerId: string | null, referrerName?: string) => void;
}

export function useReferrerComboboxController({
  value,
  onChange,
}: IUseReferrerComboboxController) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { referrers, isReferrersLoading } = useReferrers();
  const { createReferrerAsync, createReferrerIsPending } = useCreateReferrer();
  const { updateReferrerAsync, updateReferrerIsPending } = useUpdateReferrer();
  const { deleteReferrerAsync, deleteReferrerIsPending } = useDeleteReferrer();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [editingReferrerId, setEditingReferrerId] = useState<string | null>(
    null,
  );
  const [showForm, setShowForm] = useState(false);
  const [isDeleteConfirming, setIsDeleteConfirming] = useState(false);

  const form = useForm<IReferrerFormValues>({ defaultValues: EMPTY_FORM });

  const isEditing = editingReferrerId !== null;
  const isFormBusy =
    createReferrerIsPending ||
    updateReferrerIsPending ||
    deleteReferrerIsPending;

  const selectedReferrer = useMemo(
    () => referrers.find((referrer) => referrer.id === value) ?? null,
    [referrers, value],
  );

  const filteredReferrers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return referrers;
    return referrers.filter((referrer) =>
      referrer.name.toLowerCase().includes(query),
    );
  }, [referrers, search]);

  const exactMatch = useMemo(
    () =>
      referrers.some(
        (referrer) =>
          referrer.name.toLowerCase() === search.trim().toLowerCase(),
      ),
    [referrers, search],
  );

  function resetForm() {
    setShowForm(false);
    setEditingReferrerId(null);
    setIsDeleteConfirming(false);
    form.reset(EMPTY_FORM);
  }

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) {
      resetForm();
      setSearch('');
    }
  }

  function handleSelect(referrer: IReferrer) {
    onChange(referrer.id, referrer.name);
    handleOpenChange(false);
  }

  function handleClear() {
    onChange(null, undefined);
  }

  function openCreateForm() {
    setEditingReferrerId(null);
    setIsDeleteConfirming(false);
    form.reset({ ...EMPTY_FORM, name: search.trim() });
    setShowForm(true);
  }

  function openEditForm(referrer: IReferrer) {
    setEditingReferrerId(referrer.id);
    setIsDeleteConfirming(false);
    form.reset({
      name: referrer.name,
      kind: referrer.kind,
      phone: referrer.phone ?? '',
      instagram: referrer.instagram ?? '',
    });
    setShowForm(true);
  }

  const handleSubmit = form.handleSubmit(async (values) => {
    const name = values.name.trim();
    if (!name) return;

    const payload = {
      name,
      kind: values.kind,
      phone: values.phone.trim() || null,
      instagram: values.instagram.trim() || null,
    };

    try {
      if (editingReferrerId) {
        await updateReferrerAsync({ id: editingReferrerId, data: payload });
        toast(t('referrer.toast.updated'), 'success');
        if (editingReferrerId === value) {
          onChange(editingReferrerId, name);
        }
        resetForm();
        return;
      }

      const created = await createReferrerAsync(payload);
      toast(t('referrer.toast.created'), 'success');
      onChange(created.id, created.name);
      setSearch('');
      handleOpenChange(false);
    } catch {
      toast(
        editingReferrerId
          ? t('referrer.toast.error.update')
          : t('referrer.toast.error.create'),
        'error',
      );
    }
  });

  async function handleDelete() {
    if (!editingReferrerId) return;

    if (!isDeleteConfirming) {
      setIsDeleteConfirming(true);
      return;
    }

    try {
      await deleteReferrerAsync(editingReferrerId);
      toast(t('referrer.toast.deleted'), 'success');
      if (editingReferrerId === value) {
        onChange(null, undefined);
      }
      resetForm();
    } catch {
      toast(t('referrer.toast.error.delete'), 'error');
    }
  }

  return {
    t,
    form,
    open,
    search,
    showForm,
    isEditing,
    isFormBusy,
    isDeleteConfirming,
    isReferrersLoading,
    selectedReferrer,
    filteredReferrers,
    exactMatch,
    setSearch,
    handleOpenChange,
    handleSelect,
    handleClear,
    openCreateForm,
    openEditForm,
    toggleDeleteConfirm: handleDelete,
    handleSubmit,
    resetForm,
  };
}
